import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTenant } from '../contexts/TenantContext'
import logo from '../assets/El CapIT_logo_top_left.png'
import { Button } from '../components/Button'
import { Input } from '../components/Input'
import { Card } from '../components/Card'
import { Modal } from '../components/Modal'
import { isOnMainDomain, getSubdomainUrl } from '../utils/tenantRedirect'

const STORY_CONTENT = `It started with a bell. In 2004, inside a three-story sporting goods store somewhere in the Midwest, a young founder stood at the base of a 40-foot climbing wall. At the top hung a bell—ring it, and you'd conquered the wall. Simple. Clear. Satisfying.

That moment sparked something. Not just the thrill of reaching the top, but the realization that climbing gyms needed better tools. Tools that could capture that same clarity, that same sense of achievement, but for the people running these spaces.

Fast forward to today. El CapIT was born from that insight—a platform built specifically for climbing gym operators. We're not trying to be everything to everyone. We're focused on one thing: making gym management as smooth and intuitive as a perfect climb.

Our platform handles memberships, check-ins, waivers, and all the operational details that keep a gym running. But more than that, it's designed by people who understand the climbing community. We know that every gym has its own culture, its own vibe, and our tools flex to fit that.

We're BuilderBias through and through—we believe in building things that matter, with care and precision. No bloat. No unnecessary complexity. Just solid, reliable software that helps gym owners focus on what they do best: creating spaces where people can challenge themselves, build community, and ring their own bells.

From ringing a bell in a sporting goods store to building the future of climbing gyms. This is El CapIT.`

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false)
  const [isStoryModalOpen, setIsStoryModalOpen] = useState(false)
  const navigate = useNavigate()
  const { login, completeNewPassword } = useAuth()
  const { tenant } = useTenant()

  const isGymSubdomain = window.location.hostname.split('.').length >= 3
  const gymLogo = tenant?.branding?.logoUrl
  const gymName = tenant?.branding?.gymName

  // Force light mode on login page
  useEffect(() => {
    document.documentElement.classList.remove('dark')
    return () => {
      // Restore theme preference when leaving login page
      const stored = localStorage.getItem('user-theme-preference')
      if (stored === 'dark') {
        document.documentElement.classList.add('dark')
      }
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const returnedTenantId = await login(email, password)
      // If on the main domain and the user belongs to a tenant subdomain,
      // redirect there — cookies are already shared so they're already authenticated.
      if (isOnMainDomain() && returnedTenantId) {
        const subdomainUrl = getSubdomainUrl(returnedTenantId)
        if (subdomainUrl) {
          window.location.replace(`${subdomainUrl}/dashboard`)
          return
        }
      }
      navigate('/dashboard')
    } catch (err: any) {
      // Check if it's a new password required error
      if (err.message?.includes('NEW_PASSWORD_REQUIRED') || err.name === 'NewPasswordRequiredError') {
        setNeedsPasswordChange(true)
        setError('')
      } else {
        setError(err.message || 'Failed to login. Please check your credentials.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    // Validate password requirements
    const hasUppercase = /[A-Z]/.test(newPassword)
    const hasLowercase = /[a-z]/.test(newPassword)
    const hasNumber = /[0-9]/.test(newPassword)
    const hasSymbol = /[^A-Za-z0-9]/.test(newPassword)

    if (!hasUppercase || !hasLowercase || !hasNumber || !hasSymbol) {
      setError('Password must contain uppercase, lowercase, number, and symbol')
      return
    }

    setLoading(true)
    try {
      const returnedTenantId = await completeNewPassword(newPassword)
      if (isOnMainDomain() && returnedTenantId) {
        const subdomainUrl = getSubdomainUrl(returnedTenantId)
        if (subdomainUrl) {
          window.location.replace(`${subdomainUrl}/dashboard`)
          return
        }
      }
      navigate('/dashboard')
    } catch (err: any) {
      setError(err.message || 'Failed to change password')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {isGymSubdomain && gymLogo ? (
            <img src={gymLogo} alt={gymName} className="h-24 mx-auto mb-6 object-contain" />
          ) : isGymSubdomain && gymName ? (
            <h1 className="text-3xl font-bold text-neutral-900 mb-6">{gymName}</h1>
          ) : (
            <img src={logo} alt="El CapIT" className="h-48 mx-auto mb-6" />
          )}
        </div>

        <Card padding="lg">
          {!needsPasswordChange ? (
            <>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6">
                {isGymSubdomain ? 'Staff Sign In' : 'Sign In'}
              </h2>
              
              {error && (
                <div className="mb-6 p-4 bg-error-light/20 border border-error-DEFAULT rounded-lg">
                  <p className="text-sm text-error-dark">{error}</p>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  type="email"
                  label="Email"
                  value={email}
                  onChange={setEmail}
                  placeholder="you@example.com"
                  required
                  disabled={loading}
                />

                <Input
                  type="password"
                  label="Password"
                  value={password}
                  onChange={setPassword}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                />

                <Button 
                  type="submit" 
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={loading}
                  loading={loading}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-neutral-900 mb-2">Change Password</h2>
              <p className="text-neutral-600 mb-6">Please set a new password for your account</p>
              
              {error && (
                <div className="mb-6 p-4 bg-error-light/20 border border-error-DEFAULT rounded-lg">
                  <p className="text-sm text-error-dark">{error}</p>
                </div>
              )}
              
              <form onSubmit={handlePasswordChange} className="space-y-6">
                <Input
                  type="password"
                  label="New Password"
                  value={newPassword}
                  onChange={setNewPassword}
                  placeholder="••••••••"
                  helperText="Must be at least 8 characters with uppercase, lowercase, number, and symbol"
                  required
                  disabled={loading}
                />

                <Input
                  type="password"
                  label="Confirm Password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  placeholder="••••••••"
                  required
                  disabled={loading}
                />

                <Button 
                  type="submit" 
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={loading}
                  loading={loading}
                >
                  {loading ? 'Changing Password...' : 'Change Password'}
                </Button>
              </form>
            </>
          )}
        </Card>

        <div className="text-center mt-4 space-y-2">
          {!isGymSubdomain && (
            <button
              onClick={() => setIsStoryModalOpen(true)}
              className="text-neutral-500 text-sm hover:text-primary-dark hover:underline cursor-pointer transition-colors duration-200"
              style={{ textShadow: '1px 1px 2px rgba(255, 255, 255, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.2)' }}
            >
              The Story of El CapIT
            </button>
          )}
          <p className="text-neutral-500 text-sm" style={{ textShadow: '1px 1px 2px rgba(255, 255, 255, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.2)' }}>
            {isGymSubdomain
              ? <>Powered by <a href="https://elcapit.com" target="_blank" rel="noopener noreferrer" className="text-primary-DEFAULT hover:text-primary-dark underline">El CapIT</a></>
              : <>A <a href="https://builderbias.com" target="_blank" rel="noopener noreferrer" className="text-primary-DEFAULT hover:text-primary-dark underline">BuilderBias</a> System</>
            }
          </p>
        </div>
      </div>

      <Modal
        isOpen={isStoryModalOpen}
        onClose={() => setIsStoryModalOpen(false)}
        title="The Story of El CapIT"
        size="lg"
        closeOnOverlayClick={true}
        closeOnEsc={true}
      >
        <div className="prose prose-neutral max-w-none">
          {STORY_CONTENT.split('\n\n').map((paragraph, index) => (
            <p key={index} className="mb-4 text-neutral-700 dark:text-neutral-300 leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>
      </Modal>
    </div>
  )
}
